import { useEffect, useState } from 'react'
import { Trash2, StickyNote } from 'lucide-react'
import { api } from '@/lib/api'

interface Note {
  id: string
  body: string
  authorUserId: string | null
  aiTagged: boolean
  createdAt: string
}

interface NotesSectionProps {
  conversationId: string
}

export function NotesSection({ conversationId }: NotesSectionProps) {
  const [items, setItems] = useState<Note[]>([])
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)

  async function load() {
    try {
      const r = await api<{ items: Note[] }>(
        `/v1/marketing/inbox/conversations/${conversationId}/notes`,
      )
      setItems(r.items)
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!draft.trim() || busy) return
    setBusy(true)
    try {
      await api(`/v1/marketing/inbox/conversations/${conversationId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ body: draft.trim() }),
      })
      setDraft('')
      await load()
    } finally {
      setBusy(false)
    }
  }

  async function remove(id: string) {
    await api(`/v1/marketing/inbox/conversations/${conversationId}/notes/${id}`, {
      method: 'DELETE',
    })
    await load()
  }

  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-700/60 mb-1.5 flex items-center gap-1">
        <StickyNote className="size-3" />
        notas internas
        <span className="ml-auto text-ink-700/30">el cliente no las ve</span>
      </div>
      <form onSubmit={(e) => void submit(e)} className="mb-2">
        <textarea
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="añadir nota interna…"
          className="w-full border border-ink-900/15 rounded px-2 py-1 text-xs resize-none"
        />
        <button
          type="submit"
          disabled={!draft.trim() || busy}
          className="bp-btn-primary mt-1 w-full justify-center"
        >
          {busy ? '…' : 'Añadir nota'}
        </button>
      </form>
      <ul className="space-y-1">
        {items.length === 0 && (
          <li className="text-xs text-ink-700/40 text-center py-2">(sin notas)</li>
        )}
        {items.map((n) => (
          <li key={n.id} className="bp-card p-2 text-xs bg-amber-50/30 border-amber-200/50">
            <div className="whitespace-pre-wrap text-ink-900">{n.body}</div>
            <div className="mt-1 flex items-center gap-2 text-[10px] font-mono text-ink-700/50">
              <span>{new Date(n.createdAt).toLocaleString('es-ES')}</span>
              {n.aiTagged && <span className="bp-pill">IA</span>}
              <button
                onClick={() => void remove(n.id)}
                className="ml-auto hover:text-red-600"
                type="button"
                aria-label="eliminar nota"
              >
                <Trash2 className="size-3" />
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
